import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import ArticleCard from '@/components/ArticleCard';
import Sidebar from '@/components/Sidebar';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Loader2, TrendingUp, Star, Flame } from 'lucide-react';

function HomePage({ user, token, openAuthModal }) {
  const [activeTab, setActiveTab] = useState('main');
  const [articles, setArticles] = useState([]);
  const [savedArticleIds, setSavedArticleIds] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasNewArticles, setHasNewArticles] = useState(false);
  const [latestArticleId, setLatestArticleId] = useState(null);

  const ITEMS_PER_PAGE = 10;

  // Load saved articles if user is logged in
  useEffect(() => {
    if (token) {
      axios.get(`${API}/user/saved-article-ids`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(response => setSavedArticleIds(response.data))
      .catch(error => console.error('Error loading saved articles:', error));
    } else {
      setSavedArticleIds([]);
    }
  }, [token]);

  // Load initial articles when tab changes
  useEffect(() => {
    setArticles([]);
    setPage(0);
    setHasMore(true);
    loadArticles(0, true);
  }, [activeTab]);

  // Check for new articles every 30 seconds
  useEffect(() => {
    if (latestArticleId && !isRefreshing) {
      const interval = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/articles/check-new`, {
            params: { last_seen_id: latestArticleId }
          });
          if (response.data.has_new) {
            setHasNewArticles(true);
          }
        } catch (error) {
          console.error('Error checking for new articles:', error);
        }
      }, 30000); // Check every 30 seconds

      return () => clearInterval(interval);
    }
  }, [latestArticleId, isRefreshing]);

  const loadArticles = async (pageNum, reset = false) => {
    try {
      const response = await axios.get(`${API}/articles`, {
        params: {
          feed_type: activeTab,
          skip: pageNum * ITEMS_PER_PAGE,
          limit: ITEMS_PER_PAGE
        }
      });
      
      const newArticles = response.data;
      
      if (reset) {
        setArticles(newArticles);
        if (newArticles.length > 0) {
          setLatestArticleId(newArticles[0].id);
        }
      } else {
        setArticles(prev => [...prev, ...newArticles]);
      }
      
      setHasMore(newArticles.length === ITEMS_PER_PAGE);
    } catch (error) {
      console.error('Error loading articles:', error);
    }
  };

  const fetchMoreArticles = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadArticles(nextPage);
  };

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    setHasNewArticles(false);
    setPage(0);
    await loadArticles(0, true);
    
    // Reload saved articles if logged in
    if (token) {
      try {
        const response = await axios.get(`${API}/user/saved-article-ids`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSavedArticleIds(response.data);
      } catch (error) {
        console.error('Error reloading saved articles:', error);
      }
    }
    
    setTimeout(() => setIsRefreshing(false), 500);
  }, [token, activeTab]);

  const onToggleSave = (articleId, isSaved) => {
    if (isSaved) {
      setSavedArticleIds(prev => [...prev, articleId]);
    } else {
      setSavedArticleIds(prev => prev.filter(id => id !== articleId));
    }
  };

  const onPromote = (articleId) => {
    // Update the article's promoted status in the local state
    setArticles(prev => prev.map(article => 
      article.id === articleId 
        ? { ...article, promoted: new Date().toISOString() }
        : article
    ));
  };

  const tabs = [
    { id: 'main', label: 'Main Feed', icon: Flame },
    { id: 'popular', label: 'Popular', icon: TrendingUp },
    { id: 'promoted', label: 'Promoted', icon: Star }
  ];

  // Pull to refresh handler
  useEffect(() => {
    let startY = 0;
    let pulling = false;

    const handleTouchStart = (e) => {
      if (window.scrollY === 0) {
        startY = e.touches[0].clientY;
        pulling = true;
      }
    };

    const handleTouchMove = (e) => {
      if (!pulling) return;
      
      const currentY = e.touches[0].clientY;
      const pullDistance = currentY - startY;
      
      if (pullDistance > 100 && window.scrollY === 0) {
        handleRefresh();
        pulling = false;
      }
    };

    const handleTouchEnd = () => {
      pulling = false;
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleRefresh]);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Tabs */}
      <div className="sticky top-14 sm:top-16 z-40 bg-background/95 backdrop-blur border-b border-border">
        <div className="container mx-auto px-3 sm:px-4">
          <div className="flex gap-1 sm:gap-2 overflow-x-auto no-scrollbar">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base font-medium whitespace-nowrap transition-colors border-b-2 ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent hover:text-primary'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Refresh Indicator */}
      {isRefreshing && (
        <div className="flex justify-center py-4">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      )}

      {/* New Articles Available Banner */}
      {hasNewArticles && !isRefreshing && (
        <div className="container mx-auto px-3 sm:px-4">
          <div className="pt-3 sm:pt-4 pb-2">
            <button
              onClick={handleRefresh}
              className="w-full text-white py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg text-sm sm:text-base font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(145deg, #762b9e, #4235c1)' }}
              data-testid="new-articles-banner"
            >
              <Flame className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden xs:inline">New Articles Available - Click to Refresh</span>
              <span className="xs:hidden">New Articles - Refresh</span>
            </button>
          </div>
        </div>
      )}

      {/* Articles Grid with Sidebar */}
      <div className="container mx-auto px-3 sm:px-4 pb-8 mt-2" style={{ paddingTop: '10px' }}>
        <div className="flex gap-4 lg:gap-8">
          {/* Main Content - Articles Feed */}
          <div className="flex-1 min-w-0">
            <InfiniteScroll
              dataLength={articles.length}
              next={fetchMoreArticles}
              hasMore={hasMore}
              loader={
                <div className="flex justify-center py-8" data-testid="loading-more">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              }
              endMessage={
                <p className="text-center text-muted-foreground py-8">
                  You've reached the end! 🎮
                </p>
              }
            >
              <div className="grid grid-cols-1 gap-4">
                {articles.map(article => (
                  <ArticleCard
                    key={article.id}
                    article={article}
                    token={token}
                    user={user}
                    savedArticleIds={savedArticleIds}
                    onToggleSave={onToggleSave}
                    onPromote={onPromote}
                    openAuthModal={openAuthModal}
                  />
                ))}
              </div>
            </InfiniteScroll>
          </div>

          {/* Sidebar - Desktop Only */}
          <Sidebar />
        </div>
      </div>
    </div>
  );
}

export default HomePage;